/**
 * Main Search block behaviour.
 *
 * Vanilla, dependency-free script (mirrors the pattern used by the
 * Professionals Grid / Blog Grid filter scripts in this theme) so it can be
 * dropped on any page, including ones that don't load the Professionals
 * Grid at all (e.g. the homepage).
 *
 * Requires belgian-regions.js for province matching.
 *
 * Each `[data-main-search]` element on the page is initialised independently
 * so the same block can appear more than once (e.g. on the homepage and
 * again on the professionals directory page).
 */
(function () {
  'use strict';

  var regionsApi = window.BelgianRegions;

  function normalize(value) {
    return (value || '').toString().trim().toLowerCase();
  }

  function splitCommaValues(value) {
    return (value || '')
      .split(',')
      .map(function (part) {
        return part.trim();
      })
      .filter(Boolean);
  }

  function readData(root) {
    var script = root.querySelector('[data-main-search-data]');
    if (!script) return { professions: [], professionals: [] };
    try {
      var parsed = JSON.parse(script.textContent || '{}');
      return {
        professions: Array.isArray(parsed.professions) ? parsed.professions : [],
        professionals: Array.isArray(parsed.professionals) ? parsed.professionals : []
      };
    } catch (e) {
      return { professions: [], professionals: [] };
    }
  }

  function initMainSearch(root) {
    if (!regionsApi) return;

    var data = readData(root);
    var whoInput = root.querySelector('[data-main-search-who-input]');
    var whoSuggestions = root.querySelector('[data-main-search-suggestions]');
    var regionInput = root.querySelector('[data-main-search-city-input]');
    var regionSuggestions = root.querySelector('[data-main-search-city-suggestions]');
    var pillsWrapper = root.querySelector('[data-main-search-pills]');
    var pills = Array.prototype.slice.call(root.querySelectorAll('[data-main-search-pill]'));
    var showAllButton = root.querySelector('[data-main-search-show-all]');
    var submitButton = root.querySelector('[data-main-search-submit]');
    var resultsUrl = root.getAttribute('data-results-url') || '/pages/les-professionnel-les';
    var noResultsLabel = root.getAttribute('data-no-results-label') || 'No results found';
    var rootClass = root.className.split(' ')[0];
    var weglotRefreshQueued = false;
    var weglotRefreshRetryTimer = null;

    function getRegionOptions() {
      return regionsApi.getRegionOrder().filter(function (province) {
        return data.professionals.some(function (professional) {
          return regionsApi.localisationMatchesRegion(professional.localisation, province);
        });
      });
    }

    function refreshWeglotTranslations(attempt) {
      var tries = typeof attempt === 'number' ? attempt : 0;
      if (
        !window.Weglot ||
        typeof Weglot.getCurrentLang !== 'function' ||
        typeof Weglot.switchTo !== 'function'
      ) {
        if (tries < 10 && !weglotRefreshRetryTimer) {
          weglotRefreshRetryTimer = setTimeout(function () {
            weglotRefreshRetryTimer = null;
            refreshWeglotTranslations(tries + 1);
          }, 300);
        }
        return;
      }

      if (weglotRefreshQueued) return;
      weglotRefreshQueued = true;
      requestAnimationFrame(function () {
        var current = Weglot.getCurrentLang();
        if (current) {
          Weglot.switchTo(current);
        }
        weglotRefreshQueued = false;
      });
    }

    var openPanel = null;

    function closePanel(panel) {
      if (!panel) return;
      panel.classList.remove('is-open');
      panel.innerHTML = '';
      if (openPanel === panel) openPanel = null;
    }

    function closeAllPanels() {
      closePanel(whoSuggestions);
      closePanel(regionSuggestions);
    }

    function getSuggestionDisplayLabel(button) {
      if (!button) return '';
      var labelEl = button.querySelector('.' + rootClass + '__suggestion-label');
      return labelEl ? labelEl.textContent.trim() : '';
    }

    function createSuggestionButton(item, onSelect, panel) {
      var button = document.createElement('button');
      button.type = 'button';
      button.setAttribute('role', 'option');
      button.className = rootClass + '__suggestion';

      if (item.disabled) {
        button.disabled = true;
        button.className += ' ' + rootClass + '__suggestion--disabled';
        button.setAttribute('aria-disabled', 'true');
      }

      var labelEl = document.createElement('span');
      labelEl.className = rootClass + '__suggestion-label';
      labelEl.textContent = item.label;
      button.appendChild(labelEl);

      if (item.meta) {
        var metaEl = document.createElement('span');
        metaEl.className = rootClass + '__suggestion-meta';
        metaEl.textContent = item.meta;
        button.appendChild(metaEl);
      }

      if (!item.disabled) {
        button.addEventListener('click', function () {
          onSelect(item, button);
          closePanel(panel);
        });
      }

      return button;
    }

    function appendSuggestionSection(panel, headingText, items, onSelect) {
      if (!items.length) return;

      var heading = document.createElement('p');
      heading.className = rootClass + '__suggestions-section-heading';
      heading.textContent = headingText;
      panel.appendChild(heading);

      items.forEach(function (item) {
        panel.appendChild(createSuggestionButton(item, onSelect, panel));
      });
    }

    function openSuggestionsPanel(panel, items, onSelect) {
      closeAllPanels();
      if (!panel) return;

      panel.innerHTML = '';

      if (!items.length) {
        var emptyEl = document.createElement('p');
        emptyEl.className = rootClass + '__no-results';
        emptyEl.textContent = noResultsLabel;
        panel.appendChild(emptyEl);
      } else {
        items.slice(0, 20).forEach(function (item) {
          panel.appendChild(createSuggestionButton(item, onSelect, panel));
        });
      }

      panel.classList.add('is-open');
      openPanel = panel;
      refreshWeglotTranslations();
    }

    function appendSuggestionDivider(panel) {
      var divider = document.createElement('hr');
      divider.className = rootClass + '__suggestions-divider';
      divider.setAttribute('aria-hidden', 'true');
      panel.appendChild(divider);
    }

    function openWhoSuggestionsPanel(panel, professionGroups, professionalItems, onSelect) {
      closeAllPanels();
      if (!panel) return;

      panel.innerHTML = '';

      var professionalsHeading = root.getAttribute('data-professionals-heading') || 'Professionals';
      var hasContent = false;

      professionGroups.forEach(function (group) {
        if (!group.items.length) return;
        if (hasContent) appendSuggestionDivider(panel);
        appendSuggestionSection(panel, group.heading, group.items, onSelect);
        hasContent = true;
      });

      if (!hasContent && !professionalItems.length) {
        var emptyEl = document.createElement('p');
        emptyEl.className = rootClass + '__no-results';
        emptyEl.textContent = noResultsLabel;
        panel.appendChild(emptyEl);
      } else if (professionalItems.length) {
        if (hasContent) appendSuggestionDivider(panel);
        appendSuggestionSection(panel, professionalsHeading, professionalItems, onSelect);
      }

      panel.classList.add('is-open');
      openPanel = panel;
      refreshWeglotTranslations();
    }

    function buildProfessionCatalog() {
      var catalog = Object.create(null);

      data.professions.forEach(function (entry) {
        if (!entry || !entry.label) return;

        var key = normalize(entry.label);
        if (!key || catalog[key]) return;

        catalog[key] = {
          label: entry.label,
          healthcare: normalize(entry.category) === 'healthcare'
        };
      });

      return catalog;
    }

    var professionCatalog = buildProfessionCatalog();

    function isOsteopathProfession(label) {
      var key = normalize(label);
      return key.indexOf('osteopath') !== -1 || key.indexOf('osteopathe') !== -1;
    }

    function isPhysiotherapistProfession(label) {
      var key = normalize(label);
      return key.indexOf('physiotherapist') !== -1 || key.indexOf('physiotherapeute') !== -1;
    }

    function isFertilityPractitionerProfession(label) {
      var key = normalize(label);
      return key.indexOf('fertility') !== -1 &&
        (key.indexOf('practitioner') !== -1 || key.indexOf('practition') !== -1);
    }

    function sortProfessionLabels(labels) {
      var sorter = function (a, b) {
        return a.localeCompare(b, undefined, { sensitivity: 'base' });
      };

      var osteopath = null;
      var fertility = null;
      var rest = [];

      labels.forEach(function (label) {
        if (isOsteopathProfession(label)) {
          osteopath = label;
        } else if (isFertilityPractitionerProfession(label)) {
          fertility = label;
        } else {
          rest.push(label);
        }
      });

      rest.sort(sorter);

      if (osteopath) {
        rest.splice(Math.min(1, rest.length), 0, osteopath);
      }

      if (fertility) {
        var physioIndex = rest.findIndex(isPhysiotherapistProfession);
        if (physioIndex !== -1) {
          rest.splice(physioIndex + 1, 0, fertility);
        } else {
          rest.push(fertility);
        }
      }

      return rest;
    }

    function getProfessionLabels(query) {
      var normalizedQuery = normalize(query);
      var entries = [];

      data.professions.forEach(function (entry) {
        if (!entry || !entry.label) return;
        if (normalizedQuery && normalize(entry.label).indexOf(normalizedQuery) === -1) return;
        entries.push(entry);
      });

      var labelToCategory = Object.create(null);
      entries.forEach(function (entry) {
        labelToCategory[normalize(entry.label)] = normalize(entry.category);
      });

      var sortedLabels = sortProfessionLabels(entries.map(function (entry) {
        return entry.label;
      }));

      var healthcare = [];
      var wellbeing = [];

      sortedLabels.forEach(function (label) {
        if (labelToCategory[normalize(label)] === 'healthcare') {
          healthcare.push(label);
        } else {
          wellbeing.push(label);
        }
      });

      return { healthcare: healthcare, wellbeing: wellbeing };
    }

    function toProfessionItems(labels, availableProfessionKeys) {
      return labels.map(function (profession) {
        var disabled =
          availableProfessionKeys !== null && !availableProfessionKeys[normalize(profession)];
        return { label: profession, meta: '', type: 'profession', value: profession, disabled: disabled };
      });
    }

    function buildProfessionGroups(query, grouped, availableProfessionKeys) {
      var lists = getProfessionLabels(query);
      var professionsHeading = root.getAttribute('data-professions-heading') || 'Professions';
      var healthcareHeading = root.getAttribute('data-healthcare-heading') || 'Healthcare';
      var wellbeingHeading = root.getAttribute('data-wellbeing-heading') || 'Wellbeing';

      if (grouped) {
        return [
          {
            heading: healthcareHeading,
            items: toProfessionItems(lists.healthcare, availableProfessionKeys)
          },
          {
            heading: wellbeingHeading,
            items: toProfessionItems(lists.wellbeing, availableProfessionKeys)
          }
        ];
      }

      return [
        {
          heading: professionsHeading,
          items: toProfessionItems(
            sortProfessionLabels(lists.healthcare.concat(lists.wellbeing)),
            availableProfessionKeys
          )
        }
      ];
    }

    function setWhoSelection(type, value, url, handle, displayValue) {
      if (!whoInput) return;
      whoInput.dataset.whoType = type || '';
      whoInput.dataset.whoValue = value || '';
      whoInput.dataset.whoUrl = url || '';
      whoInput.dataset.whoHandle = handle || '';
      whoInput.dataset.whoDisplay = displayValue || value || '';
    }

    function clearWhoSelection() {
      if (!whoInput) return;
      whoInput.value = '';
      setWhoSelection('', '', '', '');
    }

    function clearRegionSelection() {
      if (!regionInput) return;
      regionInput.value = '';
      setRegionSelection('');
    }

    function getSelectedWhoValue() {
      if (!whoInput) return '';
      var visible = (whoInput.value || '').trim();
      if (!visible) {
        if (whoInput.dataset.whoValue) {
          setWhoSelection('', '', '', '');
        }
        return '';
      }
      return (whoInput.dataset.whoValue || visible).trim();
    }

    function getSelectedWhoType() {
      if (!getSelectedWhoValue()) return '';
      return whoInput ? whoInput.dataset.whoType || '' : '';
    }

    function findProfessionalByName(name) {
      var query = normalize(name);
      if (!query) return null;

      var exactMatches = data.professionals.filter(function (professional) {
        return professional && normalize(professional.name) === query;
      });

      if (exactMatches.length === 1) return exactMatches[0];
      return null;
    }

    function findProfessionByName(name) {
      var query = normalize(name);
      if (!query) return null;

      if (professionCatalog[query]) return professionCatalog[query].label;

      return null;
    }

    function professionalHasProfession(professional, professionName) {
      var wanted = normalize(professionName);
      if (!wanted || !professional) return false;

      return splitCommaValues(professional.profession).some(function (profession) {
        return normalize(profession) === wanted;
      });
    }

    function getSelectedPills() {
      return pills
        .filter(function (pill) { return pill.getAttribute('aria-pressed') === 'true'; })
        .map(function (pill) { return normalize(pill.getAttribute('data-value') || ''); })
        .filter(Boolean);
    }

    function professionalMatchesPills(professional, selectedPills) {
      if (!selectedPills.length) return true;
      var profSupports = splitCommaValues(professional.supports || '').map(normalize);
      return selectedPills.some(function (pill) {
        return profSupports.indexOf(pill) !== -1;
      });
    }

    function addRegionKeys(regionMap, localisationValue) {
      regionsApi.resolveRegionsFromLocalisation(localisationValue).forEach(function (region) {
        regionMap[normalize(region)] = true;
      });
    }

    function professionalInRegion(professional, regionName) {
      if (!professional || !regionName) return false;
      return regionsApi.localisationMatchesRegion(professional.localisation, regionName);
    }

    function getSelectedRegionValue() {
      if (!regionInput) return '';
      var visible = (regionInput.value || '').trim();
      if (!visible) {
        if (regionInput.dataset.regionValue) {
          setRegionSelection('');
        }
        return '';
      }
      return (regionInput.dataset.regionValue || visible).trim();
    }

    function getAvailableProfessionKeys() {
      var regionValue = getSelectedRegionValue();
      var selectedPills = getSelectedPills();

      if (!regionValue && !selectedPills.length) return null;

      var availableProfessions = Object.create(null);

      data.professionals.forEach(function (professional) {
        if (regionValue && !professionalInRegion(professional, regionValue)) return;
        if (!professionalMatchesPills(professional, selectedPills)) return;

        splitCommaValues(professional.profession).forEach(function (professionLabel) {
          if (professionLabel) {
            availableProfessions[normalize(professionLabel)] = true;
          }
        });
      });

      return availableProfessions;
    }

    function getAvailableRegionKeys() {
      if (!whoInput) return null;

      var whoType = getSelectedWhoType();
      var whoValue = getSelectedWhoValue();
      if (!whoValue) return null;

      var availableRegions = Object.create(null);

      if (whoType === 'profession') {
        data.professionals.forEach(function (professional) {
          if (!professionalHasProfession(professional, whoValue)) return;
          addRegionKeys(availableRegions, professional.localisation);
        });
        return availableRegions;
      }

      if (whoType === 'name') {
        var matchedProfessional = findProfessionalByName(whoValue);
        if (matchedProfessional) {
          addRegionKeys(availableRegions, matchedProfessional.localisation);
          return availableRegions;
        }

        var matchedProfession = findProfessionByName(whoValue);
        if (matchedProfession) {
          data.professionals.forEach(function (professional) {
            if (!professionalHasProfession(professional, matchedProfession)) return;
            addRegionKeys(availableRegions, professional.localisation);
          });
          return availableRegions;
        }
      }

      return null;
    }

    function syncWhoAndRegionSelections() {
      if (!whoInput || !regionInput) return;

      var regionValue = getSelectedRegionValue();
      var regionKey = normalize(regionValue);
      var whoType = getSelectedWhoType();
      var whoValue = normalize(getSelectedWhoValue());

      if (whoType === 'profession' && whoValue) {
        var availableProfessions = getAvailableProfessionKeys();
        if (availableProfessions && !availableProfessions[whoValue]) {
          whoInput.value = '';
          setWhoSelection('', '', '', '');
        }
      }

      if (regionKey) return;

      if (!whoValue) return;

      var availableRegions = getAvailableRegionKeys();
      if (!availableRegions) return;

      if (regionKey && !availableRegions[regionKey]) {
        regionInput.value = '';
        setRegionSelection('');
      }
    }

    function resolveSearchDestination() {
      var whoType = getSelectedWhoType();
      var whoValue = getSelectedWhoValue();
      var profileUrl = whoInput && whoValue ? (whoInput.dataset.whoUrl || '').trim() : '';

      if (whoType === 'name' && whoValue) {
        if (profileUrl) return profileUrl;

        var matchedProfessional = findProfessionalByName(whoValue);
        if (matchedProfessional && matchedProfessional.profileUrl) {
          return matchedProfessional.profileUrl;
        }
      }

      return buildResultsUrl();
    }

    function setRegionSelection(value) {
      if (!regionInput) return;
      regionInput.dataset.regionValue = value || '';
    }

    function showWhoSuggestions(query) {
      if (!whoSuggestions || !whoInput) return;

      var whoType = getSelectedWhoType();
      var selectedWhoValue = normalize(getSelectedWhoValue());
      var normalizedQuery = normalize(query);
      var professionalItems = [];
      var professionQuery = query;
      var groupedProfessions = !normalizedQuery;

      // Reopen the full profession list when a profession is already selected.
      var selectedWhoDisplay = normalize(whoInput.dataset.whoDisplay || whoInput.value || '');
      if (
        whoType === 'profession' &&
        selectedWhoValue &&
        (selectedWhoValue === normalizedQuery || selectedWhoDisplay === normalizedQuery)
      ) {
        professionQuery = '';
        groupedProfessions = true;
      }

      var availableProfessionKeys = getAvailableProfessionKeys();
      var selectedRegionValue = getSelectedRegionValue();
      var professionGroups = buildProfessionGroups(
        professionQuery,
        groupedProfessions,
        availableProfessionKeys
      );

      if (normalizedQuery && !(whoType === 'profession' && selectedWhoValue === normalizedQuery)) {
        data.professionals.forEach(function (professional) {
          if (!professional || !professional.name) return;
          if (normalize(professional.name).indexOf(normalizedQuery) === -1) return;

          var disabled =
            selectedRegionValue !== '' && !professionalInRegion(professional, selectedRegionValue);

          professionalItems.push({
            label: professional.name,
            meta: professional.profession || '',
            type: 'name',
            value: professional.name,
            url: professional.profileUrl || '',
            handle: professional.handle || '',
            disabled: disabled
          });
        });
      }

      openWhoSuggestionsPanel(whoSuggestions, professionGroups, professionalItems, function (item, button) {
        var displayLabel = getSuggestionDisplayLabel(button) || item.label;
        whoInput.value = displayLabel;
        setWhoSelection(item.type, item.value, item.url, item.handle, displayLabel);
        syncWhoAndRegionSelections();

        if (item.type === 'name' && item.url) {
          window.location.href = item.url;
          return;
        }

        whoInput.focus();
      });
    }

    function showRegionSuggestions(query) {
      if (!regionSuggestions || !regionInput) return;
      var selectedRegionValue = getSelectedRegionValue();
      var normalizedQuery = normalize(query);
      var items = [];
      var availableRegions = getAvailableRegionKeys();

      // Reopen the full province list when one is already selected.
      var canonicalRegion = (regionInput.dataset.regionValue || '').trim();
      if (canonicalRegion) {
        var selectedRegionId = regionsApi.resolveRegionIdFromAlias(canonicalRegion);
        var queryRegionId = regionsApi.resolveRegionIdFromAlias(query);
        if (selectedRegionId && (!queryRegionId || selectedRegionId === queryRegionId)) {
          normalizedQuery = '';
        }
      }

      getRegionOptions().forEach(function (regionLabel) {
        if (!normalizedQuery || normalize(regionLabel).indexOf(normalizedQuery) !== -1) {
          var regionId = regionsApi.resolveRegionIdFromAlias(regionLabel);
          var canonicalRegionValue = regionId
            ? regionsApi.getRegionLabel(regionId, 'en')
            : regionLabel;
          var disabled =
            availableRegions !== null && !availableRegions[normalize(regionLabel)];
          items.push({
            label: regionLabel,
            value: canonicalRegionValue,
            disabled: disabled
          });
        }
      });

      openSuggestionsPanel(regionSuggestions, items, function (item, button) {
        regionInput.value = getSuggestionDisplayLabel(button) || item.label;
        setRegionSelection(item.value);
        syncWhoAndRegionSelections();
        regionInput.focus();
      });
    }

    if (whoInput) {
      whoInput.addEventListener('focus', function () {
        syncWhoAndRegionSelections();
        showWhoSuggestions(whoInput.value);
      });

      whoInput.addEventListener('click', function () {
        syncWhoAndRegionSelections();
        showWhoSuggestions(whoInput.value);
      });

      whoInput.addEventListener('input', function () {
        var typedValue = whoInput.value.trim();
        var matchedProfessional = findProfessionalByName(typedValue);
        if (matchedProfessional) {
          setWhoSelection(
            'name',
            matchedProfessional.name,
            matchedProfessional.profileUrl || '',
            matchedProfessional.handle || ''
          );
        } else {
          var matchedProfession = findProfessionByName(typedValue);
          if (matchedProfession) {
            setWhoSelection('profession', matchedProfession, '', '');
          } else {
            setWhoSelection('name', typedValue, '', '');
          }
        }
        syncWhoAndRegionSelections();
        showWhoSuggestions(whoInput.value);
      });

      whoInput.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') closePanel(whoSuggestions);
      });
    }

    if (regionInput) {
      regionInput.addEventListener('focus', function () {
        showRegionSuggestions(regionInput.value);
      });

      regionInput.addEventListener('click', function () {
        showRegionSuggestions(regionInput.value);
      });

      regionInput.addEventListener('input', function () {
        setRegionSelection(regionInput.value.trim());
        syncWhoAndRegionSelections();
        showRegionSuggestions(regionInput.value);
      });

      regionInput.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') closePanel(regionSuggestions);
      });
    }

    document.addEventListener('click', function (event) {
      if (!root.contains(event.target)) {
        closeAllPanels();
      }
    });

    function setPillPressed(pill, pressed) {
      pill.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    }

    pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        var isPressed = pill.getAttribute('aria-pressed') === 'true';
        setPillPressed(pill, !isPressed);
        syncWhoAndRegionSelections();
        if (openPanel === whoSuggestions && whoInput) {
          showWhoSuggestions(whoInput.value);
        }
      });
    });

    var showAllLabel = root.getAttribute('data-show-all-label') || 'Show all';
    var showLessLabel = root.getAttribute('data-show-less-label') || 'Show less';
    var collapsedPillsHeight = 0;
    var expandedPillsHeight = 0;

    function measurePillsHeight(collapsed) {
      if (!pillsWrapper) return 0;

      var previousMinHeight = pillsWrapper.style.minHeight;
      pillsWrapper.style.minHeight = '0px';

      var wasCollapsed = pillsWrapper.classList.contains('is-collapsed');
      if (collapsed) {
        pillsWrapper.classList.add('is-collapsed');
      } else {
        pillsWrapper.classList.remove('is-collapsed');
      }

      var height = pillsWrapper.offsetHeight;

      if (wasCollapsed) {
        pillsWrapper.classList.add('is-collapsed');
      } else {
        pillsWrapper.classList.remove('is-collapsed');
      }

      pillsWrapper.style.minHeight = previousMinHeight;
      return height;
    }

    function syncPillsHeights() {
      if (!pillsWrapper) return;
      collapsedPillsHeight = measurePillsHeight(true);
      expandedPillsHeight = measurePillsHeight(false);
      applyPillsMinHeight();
    }

    function applyPillsMinHeight() {
      if (!pillsWrapper) return;
      var collapsed = pillsWrapper.classList.contains('is-collapsed');
      var targetHeight = collapsed ? collapsedPillsHeight : expandedPillsHeight;
      if (targetHeight > 0) {
        pillsWrapper.style.minHeight = targetHeight + 'px';
      }
    }

    function setPillsExpanded(expanded) {
      if (!pillsWrapper || !showAllButton) return;

      if (expanded) {
        pillsWrapper.style.minHeight = expandedPillsHeight + 'px';
        pillsWrapper.classList.remove('is-collapsed');
        showAllButton.textContent = showLessLabel;
        showAllButton.setAttribute('aria-expanded', 'true');
      } else {
        pillsWrapper.classList.add('is-collapsed');
        pillsWrapper.style.minHeight = collapsedPillsHeight + 'px';
        showAllButton.textContent = '+ ' + showAllLabel;
        showAllButton.setAttribute('aria-expanded', 'false');
      }
    }

    if (showAllButton && pillsWrapper) {
      showAllButton.addEventListener('click', function () {
        setPillsExpanded(pillsWrapper.classList.contains('is-collapsed'));
      });

      var resizeTimer;
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(syncPillsHeights, 150);
      });

      root.style.minHeight = '';
      syncPillsHeights();
    }

    function getSelectedSymptoms() {
      return pills
        .filter(function (pill) {
          return pill.getAttribute('aria-pressed') === 'true';
        })
        .map(function (pill) {
          return pill.getAttribute('data-value') || '';
        })
        .filter(Boolean);
    }

    function buildResultsUrl() {
      var url;
      try {
        url = new URL(resultsUrl, window.location.origin);
      } catch (e) {
        url = new URL('/pages/les-professionnel-les', window.location.origin);
      }

      var whoType = getSelectedWhoType();
      var whoValue = getSelectedWhoValue();

      if (whoValue) {
        url.searchParams.set('who', whoValue);
        url.searchParams.set('who_type', whoType === 'profession' ? 'profession' : 'name');

        if (whoType === 'profession') {
          var professionEntry = data.professions.find(function (entry) {
            return entry && normalize(entry.label) === normalize(whoValue);
          });
          if (professionEntry && normalize(professionEntry.category) === 'wellbeing') {
            url.searchParams.set('type', 'wellbeing');
          } else if (professionEntry && normalize(professionEntry.category) === 'healthcare') {
            url.searchParams.set('type', 'healthcare');
          } else {
            url.searchParams.delete('type');
          }
        } else {
          url.searchParams.delete('type');
        }
      } else {
        url.searchParams.delete('who');
        url.searchParams.delete('who_type');
        url.searchParams.delete('type');
      }

      var region = regionInput ? (regionInput.dataset.regionValue || regionInput.value || '').trim() : '';
      url.searchParams.delete('city');
      if (region) {
        url.searchParams.set('region', region);
      } else {
        url.searchParams.delete('region');
      }

      var symptoms = getSelectedSymptoms();
      if (symptoms.length) {
        url.searchParams.set('symptoms', symptoms.join(','));
      } else {
        url.searchParams.delete('symptoms');
      }

      return url.pathname + (url.search ? url.search : '');
    }

    if (submitButton) {
      submitButton.addEventListener('click', function () {
        window.location.href = resolveSearchDestination();
      });
    }

    function restoreFromUrl() {
      var params;
      try {
        params = new URLSearchParams(window.location.search);
      } catch (e) {
        return;
      }

      var who = params.get('who');
      var whoType = params.get('who_type');
      var region = params.get('region') || regionsApi.resolveRegionFromLegacyCity(params.get('city'));
      var symptoms = params.get('symptoms');

      if (who && whoInput) {
        whoInput.value = who;
        if (whoType === 'profession') {
          setWhoSelection('profession', who, '', '');
        } else {
          var restoredProfessional = findProfessionalByName(who);
          setWhoSelection(
            'name',
            who,
            restoredProfessional ? restoredProfessional.profileUrl || '' : '',
            restoredProfessional ? restoredProfessional.handle || '' : ''
          );
        }
      } else {
        clearWhoSelection();
      }

      if (region && regionInput) {
        var regionId = regionsApi.resolveRegionIdFromAlias(region);
        var regionOptions = getRegionOptions();
        var regionMatch = regionOptions.find(function (item) {
          if (regionId) {
            return regionsApi.resolveRegionIdFromAlias(item) === regionId;
          }
          return normalize(item) === normalize(region);
        }) || regionsApi.resolveRegionFromLegacyCity(region);
        if (regionMatch && !regionOptions.some(function (item) {
          return normalize(item) === normalize(regionMatch);
        })) {
          regionMatch = '';
        }
        var canonicalRegionValue = regionId
          ? regionsApi.getRegionLabel(regionId, 'en')
          : regionMatch;
        regionInput.value = regionMatch;
        setRegionSelection(canonicalRegionValue);
      } else {
        clearRegionSelection();
      }

      pills.forEach(function (pill) {
        setPillPressed(pill, false);
      });
      if (pillsWrapper && showAllButton) {
        setPillsExpanded(false);
      }

      if (symptoms) {
        var wanted = symptoms.split(',').map(normalize).filter(Boolean);
        var hasHiddenMatch = false;
        pills.forEach(function (pill) {
          var value = normalize(pill.getAttribute('data-value'));
          if (wanted.indexOf(value) !== -1) {
            setPillPressed(pill, true);
            if (pill.classList.contains(rootClass + '__pill--overflow')) {
              hasHiddenMatch = true;
            }
          }
        });
        if (hasHiddenMatch && pillsWrapper && showAllButton) {
          setPillsExpanded(true);
        }
      }

      syncWhoAndRegionSelections();
    }

    restoreFromUrl();

    window.addEventListener('pageshow', function (event) {
      if (event.persisted) {
        restoreFromUrl();
      }
    });
  }

  function init() {
    var roots = document.querySelectorAll('[data-main-search]');
    roots.forEach(initMainSearch);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
